import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  CalendarIcon,
  DocumentTextIcon,
  EyeIcon,
  LinkIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { PageCta } from '../components/page-templates/SitePageTemplates';
import { absoluteUrl, applySeo, blogPostingJsonLd, breadcrumbJsonLd } from '../seo/tags';

interface BlogPostData {
  id: number;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  featured_image: string | null;
  author: string;
  published_at: string;
  view_count: number;
  meta_title: string;
  meta_description: string;
  updated_at?: string;
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadPost = async () => {
      if (!slug) {
        if (active) {
          setError('Post not found');
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/api/blog/posts/${slug}`, {
          signal: controller.signal,
        });
        const data = await response.json();

        if (!active) return;

        if (data.success) {
          const p: BlogPostData = data.post;
          setPost(p);
          const headline = p.meta_title || p.title;
          applySeo({
            title: headline.length <= 58 ? `${headline} | TrueLog` : headline,
            description: p.meta_description || p.excerpt,
            path: `/blog/${p.slug}`,
            image: p.featured_image || undefined,
            type: 'article',
            publishedTime: p.published_at,
            modifiedTime: p.updated_at,
            jsonLd: [
              blogPostingJsonLd({
                title: p.title,
                slug: p.slug,
                description: p.meta_description || p.excerpt,
                featured_image: p.featured_image,
                author: p.author,
                published_at: p.published_at,
                updated_at: p.updated_at,
              }),
              breadcrumbJsonLd([
                { name: 'Home', path: '/' },
                { name: 'Blog', path: '/blog' },
                { name: p.title, path: `/blog/${p.slug}` },
              ]),
            ],
          });
        } else {
          setError(data.error || 'Post not found');
          applySeo({ title: 'Post not found | TrueLog', noindex: true });
        }
      } catch (err) {
        if (!active) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError('Unable to load blog post');
        applySeo({ title: 'Post not found | TrueLog', noindex: true });
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPost();

    return () => {
      active = false;
      controller.abort();
    };
  }, [API_BASE_URL, slug]);

  // canonical production URL for the share buttons (window.location would be the
  // local server during prerendering, or the beta host on beta)
  const shareUrl = absoluteUrl(`/blog/${post?.slug || slug || ''}`);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 pt-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl animate-pulse">
          <div className="h-4 w-32 rounded bg-slate-200" />
          <div className="mt-6 h-16 w-3/4 rounded bg-slate-200" />
          <div className="mt-4 h-6 w-1/2 rounded bg-slate-200" />
          <div className="mt-10 h-[420px] rounded-[34px] bg-slate-200" />
          <div className="mt-10 space-y-4">
            <div className="h-4 rounded bg-slate-200" />
            <div className="h-4 rounded bg-slate-200" />
            <div className="h-4 w-4/5 rounded bg-slate-200" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 pt-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl rounded-[34px] border border-slate-200/80 bg-white p-10 text-center shadow-[0_28px_70px_-42px_rgba(15,23,42,0.22)]">
          <h1 className="text-4xl font-bold tracking-tight text-slate-950">Post not found</h1>
          <p className="mt-4 text-sm leading-7 text-slate-600">
            {error || 'The article you are looking for is not available right now.'}
          </p>
          <Link
            to="/blog"
            className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-[#0E9ED5] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#0b7aa6]"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <article>
        <section
          className="site3-blog-hero relative overflow-hidden bg-slate-950 pb-24 pt-32 text-white sm:pt-36"
          style={process.env.REACT_APP_LANDING === 'warehouse' ? {
            backgroundImage: `linear-gradient(90deg,#07101bea,#09131ed0),url('${process.env.PUBLIC_URL}/assets/images/warehouse/changi-ftz-racking.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 42%',
          } : undefined}
        >
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,92,242,0.26),transparent_36%),radial-gradient(circle_at_75%_15%,rgba(14,158,213,0.22),transparent_30%),linear-gradient(135deg,rgba(2,6,23,0.96),rgba(15,23,42,0.9)_42%,rgba(2,6,23,0.98))]" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
            <DocumentTextIcon className="absolute left-8 top-20 h-28 w-28 text-white/10 sm:left-16 sm:h-36 sm:w-36" />
          </div>

          <div className="relative container-custom max-w-4xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.08] px-5 py-2.5 text-sm font-medium text-white/90 backdrop-blur-xl"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to blog
            </Link>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-8 font-display text-4xl font-bold leading-[1.05] tracking-[-0.04em] text-white sm:text-5xl"
            >
              {post.title}
            </motion.h1>

            {post.excerpt ? (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.08 }}
                className="mt-6 max-w-3xl text-base leading-8 text-slate-300 sm:text-lg"
              >
                {post.excerpt}
              </motion.p>
            ) : null}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.14 }}
              className="mt-8 flex flex-wrap items-center gap-5 text-sm text-slate-300"
            >
              {post.author ? (
                <span className="inline-flex items-center gap-2">
                  <UserIcon className="h-4 w-4" />
                  {post.author}
                </span>
              ) : null}
              {post.published_at ? (
                <span className="inline-flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {formatDate(post.published_at)}
                </span>
              ) : null}
              <span className="inline-flex items-center gap-2">
                <EyeIcon className="h-4 w-4" />
                {post.view_count} views
              </span>
            </motion.div>
          </div>
        </section>

        <section className="relative z-20 -mt-14 px-4 pb-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl space-y-10">
            {post.featured_image ? (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.18 }}
                className="overflow-hidden rounded-[34px] border border-white/80 bg-white shadow-[0_32px_100px_-46px_rgba(15,23,42,0.32)]"
              >
                <img src={post.featured_image} alt={post.title} className="h-auto w-full object-cover" />
              </motion.div>
            ) : null}

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.24 }}
              className="rounded-[34px] border border-slate-200/80 bg-white p-7 shadow-[0_28px_70px_-42px_rgba(15,23,42,0.22)] sm:p-10"
            >
              <div
                className="prose prose-lg max-w-none prose-headings:text-slate-950 prose-headings:font-bold prose-p:text-slate-700 prose-p:leading-relaxed prose-a:text-[#2447d8] prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-950 prose-ul:text-slate-700 prose-ol:text-slate-700 prose-li:text-slate-700 prose-blockquote:rounded-r-xl prose-blockquote:border-cyan-400 prose-blockquote:bg-sky-50 prose-blockquote:px-4 prose-blockquote:py-2 prose-code:rounded prose-code:bg-slate-100 prose-code:px-1 prose-code:text-[#2447d8] prose-pre:border prose-pre:border-slate-200 prose-pre:bg-slate-100"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="rounded-[30px] border border-slate-200/80 bg-slate-50 p-6 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.18)]"
            >
              <h3 className="text-lg font-semibold text-slate-950">Share this article</h3>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 transition-colors hover:border-cyan-200"
                >
                  <span className="font-semibold">X</span>
                  Share on X
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 transition-colors hover:border-cyan-200"
                >
                  <span className="font-semibold">in</span>
                  Share on LinkedIn
                </a>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(shareUrl)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 transition-colors hover:border-cyan-200"
                >
                  <LinkIcon className="h-4 w-4" />
                  Copy link
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </article>

      <PageCta
        title="Need help applying this to a real programme?"
        description="If the article is relevant but your rollout has different markets, hardware categories, or timing pressure, we can work through the specifics with you directly."
        pills={['Global ICT logistics', 'Execution planning', 'Market-entry support']}
        secondaryHref="/blog"
        secondaryLabel="Back to blog"
      />
    </div>
  );
};

export default BlogPost;
